import React, { useState, useContext, useEffect } from "react";
import { Switch, Route, useHistory, Redirect } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import numberFormat from "react-number-format";

import { TypeOfUsageProperty } from "./PropertyUsage";
import { TypeOfProperty } from "./TypeOfProperty";
import { AddressType } from "./Address";
import TypeOfFinance from "./TypeOfFinance";
import PurchaseForm from "./Purchase";
import RefinanceForm from "./Refinance";
import ThankYou from "./ThankYou";
import Loader from "../../Loader";

const inIframe = () => {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
};

export const LoanFormContext = React.createContext<FormState>({
  loading: false,
  formStarted: false,
  setFormState: () => {},
});

function LoanForm() {
  console.log(`${process.env.REACT_APP_NAME} v${process.env.REACT_APP_VERSION}`)
  const [formState, setFormState] = useState<FormState>({
    loading: false,
    formStarted: false,
    dob: {
      value: {
        day: "11",
        month: "January",
        year: "2002",
      },
    },
    setFormState: () => {},
  });

  const history = useHistory();

  useEffect(() => {
    console.log(formState);
  }, [formState]);

  const getFormData = () => {
    const inFrame = inIframe();
    const formData = new FormData();

    // formData.append("provider_id", "1");
    formData.append("source", "1");
    
    if (formState.typeOfFinance) {
      if (formState.typeOfFinance.value === "purchase") {
        formData.append('loan_purpose', "Purchase");
        if (!inFrame) {
          formData.append("campaign", "159");
        }
      }
    } else {
      // Temp
      // if Refinance
      formData.append('loan_purpose', "Refinance");
      if (!inFrame) {
        formData.append("campaign", "160");
      }
    }

    if (formState.name) {
      formData.append("FirstName", formState.name?.firstName);
      formData.append("LastName", formState.name?.lastName);
    }

    if (formState.email) formData.append("Email", formState.email);
    if (formState.number) formData.append("Phone", formState.number);
    if (formState.address) {
      formData.append("Phys_Address", formState.address.address1);
      formData.append("Phys_City", formState.address.city);
      formData.append("Phys_State", formState.address.state);
      formData.append("Phys_Zip", formState.address.zip);
      formData.append("Phys_Country", 'US');
    }

    if (formState.creditHistory?.bankruptcy) {
      const yearsAgo = formState.creditHistory.bankruptcy.yearsAgo;
      formData.append(
        "Bankruptcy",
        yearsAgo + ` year${yearsAgo > 1 ? "s" : ""} ago`
      );
    }

    if (formState.creditHistory?.foreclosure) {
      const yearsAgo = formState.creditHistory.foreclosure.yearsAgo;
      formData.append(
        "Forclosure",
        yearsAgo + ` year${yearsAgo > 1 ? "s" : ""} ago`
      );
    }

    if (formState.creditScore)
      formData.append("CreditScore", formState.creditScore.value);

    if (formState.downPayment) {
      formData.append(
        "Downpayment",
        formState.downPayment.value.value +
          "% - " +
          formState.downPayment.value.upperBound +
          "%"
      );
      formData.append("Downpayment", "-");
    } else {
    }

    if (formState.estimatedPruchase) {
      const estimated = formState.estimatedPruchase.value;
      formData.append(
        "LoanAmount",
        "$" + estimated.min.toString() + " - " + "$" + estimated.min.toString()
      );
    } else {
      formData.append("LoanAmount", "-");
    }

    if (formState.combinedAnnualIncome) {
      formData.append("AnnualIncome", formState.combinedAnnualIncome.value);
    } else {
      formData.append("AnnualIncome", "-");
    }

    if (formState.typeOfFinance) {
      formData.append("loan_type", formState.typeOfFinance.value);
    } else {
      // Temp fix
      formData.append("loan_type", "refinance");
    }

    if (formState.typeOfProperty) {
      formData.append("property_type", formState.typeOfProperty.value);
    }

    if (formState.propertyEstimate) {
      const estimate = formState.propertyEstimate.value;
      formData.append(
        "property_value",
        "$" + estimate.min + " - " + "$" + estimate.max
      );
    }

    formData.append("tracking_number", uuidv4());

    // Notes
    const notes: Array<{ [key: string]: any }> = [];

    if (formState.propertyLocation) {
      notes.push({
        "In what city will the property be located?": `${formState.propertyLocation.city}, ${formState.propertyLocation.state}`,
      });
    }

    if (formState.propertyUsage) {
      notes.push({
        "How will this property be used?": formState.propertyUsage.value,
      });
    }

    if (formState.foundHome) {
      notes.push({
        "Have you already found your new home?": formState.foundHome.value,
      });
    }

    if (formState.workingWithAgent) {
      notes.push({
        "Are you currently working with a real estate agent?":
          formState.workingWithAgent.value,
      });
    }

    if (formState.planToBuy) {
      notes.push({
        "Have you already found your new home?": formState.planToBuy.value,
      });
    }

    if (formState.firstTimeBuyer) {
      notes.push({
        "Is this your first time buying a home?":
          formState.firstTimeBuyer.value,
      });
    }

    if (formState.coBorrow) {
      notes.push({
        "Will you have a co-borrower?": formState.coBorrow.value,
      });
    }

    if (formState.employment) {
      notes.push({
        "Are you currently employed?": formState.employment.value,
      });
    }

    if (formState.dob) {
      notes.push({
        "When were you born?":
          formState.dob.value.day +
          " " +
          formState.dob.value.month +
          ", " +
          formState.dob.value.year,
      });
    }

    if (formState.whyRefinance) {
      notes.push({
        "Why do you want to refinance?": formState.whyRefinance.value,
      });
    }

    if (formState.propertyZip) {
      notes.push({
        "ZIP code of the property?": formState.propertyZip.value,
      });
    }

    if (formState.secondMortgage) {
      notes.push({
        "Do you have a second mortgage?": formState.secondMortgage.value,
      });
    }

    if (formState.secondMortageRemaining) {
      notes.push({
        "What is the remaining balance on the 2nd mortgage?":
          "$" +
          formState.secondMortageRemaining.value.min.toString() +
          " - " +
          "$" +
          formState.secondMortageRemaining.value.max.toString(),
      });
    }

    if (formState.servedMilitary) {
      notes.push({
        "Have you or your spouse served in the military?":
          formState.servedMilitary.value,
      });
    }

    if (formState.haveVALoan) {
      notes.push({
        "Do you currently have a VA loan?": formState.haveVALoan,
      });
    }

    const obj: { [key: string]: any } = {};

    for (let note of notes) {
      const key: string = Object.keys(note)[0];
      obj[key] = note[key];
    }

    formData.append("notes", JSON.stringify(obj));

    // var object = {};
    // // @ts-ignore
    // formData.forEach((value, key) => (object[key] = value));
    // var json = JSON.stringify(object);
    // console.log({ json });

    return formData;
  };

  const handleSubmit = () => {
    setFormState((state) => {
      return { ...state, loading: true };
    });
    const formData = getFormData();

    fetch("https://api.leadmailbox.com/v2/leads/add/neig01/neighborhoodloanswebsite", {
      method: "POST",
      body: formData,
      mode: 'no-cors'
    })
      .then((res) => {
        return res.text();
      })
      .then(() => {
        if (inIframe()) {
          window.parent.postMessage(
            "redirect-thank-you",
            "https://neighborhoodloans.com"
          );
        } else {
          window.location.href = "https://neighborhoodloans.com/thank-you/";
          // history.push("/thank-you");
          // setFormState((state) => ({ ...state, loading: false }));
        }
      })
      .catch((err) => {
        console.log(err);
        alert("Oops! Something went wrong. Please try again.");
        setFormState((state) => {
          return { ...state, loading: false };
        });
      });
  };

  return (
    <LoanFormContext.Provider value={{ ...formState, setFormState }}>
      {formState.loading && <Loader />}
      <div>
        <Switch>
          <Route exact path="/">
            <TypeOfFinance
              onSubmit={(value) => {
                setFormState((state: FormState) => {
                  return {
                    ...state,
                    typeOfFinance: { value },
                    formStarted: true,
                    activeRoute: "TypeOfProperty",
                  };
                });
                if (value === "purchase")
                  history.push("/purchase/type-of-property");
                if (value === "refinance")
                  history.push("/refinance/type-of-property");
              }}
            />
          </Route>
          <Route exact path="/purchase/:page">
            {!formState.formStarted ? (
              <Redirect to="/" />
            ) : (
              <PurchaseForm onSubmit={handleSubmit} />
            )}
          </Route>
          <Route exact path="/refinance/:page">
            {!formState.formStarted ? (
              <Redirect to="/" />
            ) : (
              <RefinanceForm onSubmit={handleSubmit} />
            )}
          </Route>
          <Route exact path="/thank-you">
            {!formState.formStarted ? <Redirect to="/" /> : <ThankYou />}
          </Route>
        </Switch>
      </div>
    </LoanFormContext.Provider>
  );
}

export interface FormState {
  formStarted: boolean;
  steps?: {
    current: number;
    total: number;
  };
  typeOfFinance?: {
    value: string;
  };
  typeOfProperty?: {
    value: TypeOfProperty;
  };
  propertyUsage?: {
    value: TypeOfUsageProperty;
  };
  servedMilitary?: { value: string };
  foundHome?: { value: string };
  workingWithAgent?: { value: string };
  planToBuy?: { value: string };
  propertyLocation?: { city: string; state: string };
  firstTimeBuyer?: { value: string };
  coBorrow?: { value: string };
  combinedAnnualIncome?: { value: string };
  employment?: { value: string };
  creditScore?: { value: string };
  secondMortgage?: { value: string };
  dob?: {
    value: {
      month: string;
      day: string;
      year: string;
    };
  };
  estimatedPruchase?: {
    value: {
      min: number;
      max: number;
    };
  };
  propertyEstimate?: {
    value: {
      min: number;
      max: number;
    };
  };
  propertyRemainingBalance?: {
    value: {
      min: number;
      max: number;
    };
  };
  secondMortageRemaining?: {
    value: {
      min: number;
      max: number;
    };
  };
  downPayment?: {
    value: {
      value: number;
      upperBound: number;
    };
  };
  creditHistory?: {
    type: "no" | "both" | "bankruptcy" | "foreclosure";
    bankruptcy?: {
      yearsAgo: number;
    };
    foreclosure?: {
      yearsAgo: number;
    };
  };
  haveVALoan?: string;
  address?: AddressType;
  name?: {
    firstName: string;
    lastName: string;
  };
  email?: string;
  number?: string;
  loading: boolean;
  whyRefinance?: {
    value: string;
  };
  propertyZip?: {
    value: string;
  };
  setFormState: React.Dispatch<React.SetStateAction<FormState>>;
}

export default LoanForm;
